import { GenericErrorBoundary } from 'components/common-form/builder/form/GenericErrorBoundary';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { LegacyDocumentButton } from 'components/common-form/sidePanel/docToolbar/documents-legacy';
import { DocumentButton } from 'components/common-form/sidePanel/docToolbar/documents/DocumentButton';
import { SidePanelSkeleton } from 'components/common-form/sidePanel/SidePanel.skeleton';
import { FormattedMessage } from 'components/shared/Intl';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { FC } from 'react';

import { Comment } from '../Comment';
import { FightingBackJustification } from './FightingBackJustification';
import { ProductModificationButtons } from './ProductModificationButtons';
import { PublicOfferingDisclaimer } from './PublicOfferingDisclaimer';
import { ReloadInformation } from './ReloadInformation';
import { RequestId } from './RequestId';
import type { SidePanelProps } from './SidePanel.connect';
import { connectSidePanel } from './SidePanel.connect';
import { Status } from './Status';
import { ValidationErrors } from './ValidationErrors';
import { WarningCallTrader } from './WarningCallTrader';

const Component: FC<SidePanelProps> = ({
  tabId,
  status,
  isReady,
  requestId,

  canApply,

  fightingBackJustification,
  fightingBackJustificationChanged,
  fightingBackComment,
  fightingBackCommentChanged,

  hasConfirmedPublicOfferingDisclaimer,
  hasConfirmedPublicOfferingDisclaimerChanged,
  hasValidationErrors,
  showPublicOfferingDisclaimer,
  isLoading,
  showReloadInformation,

  applyProductModification,
  reloadProductModification,
}) => {
  const isNextDocumentEnabled = useToggleFeature('nextDocumentParis');

  return isLoading ? (
    <SidePanelSkeleton />
  ) : (
    <FormOrientationContext.Provider value="vertical">
      <GenericErrorBoundary key={tabId} context={{ tabId: tabId, requestId: requestId ?? '' }}>
        <div className="card bg-lvl1 border-start d-flex flex-column h-100 position-relative">
          <h5 className="card-title m-4">
            <FormattedMessage id="sidePanel.tabs.productModification" />
          </h5>
          <Status tabId={tabId} />
          <div
            id="side-panel-header"
            data-e2e="side-panel-header"
            className="mx-3 mt-3 flex-grow-1 flex-shrink-1 d-flex flex-column"
          >
            <ReloadInformation showReloadInformation={showReloadInformation} />
            <RequestId requestId={requestId} />

            <FightingBackJustification
              value={fightingBackJustification ?? ''}
              disabled={status !== 'INITIALIZED'}
              onChange={fightingBackJustificationChanged}
            />
            <Comment
              className="w-100 flex-grow-1 flex-shrink-1"
              value={fightingBackComment ?? ''}
              disabled={status !== 'INITIALIZED'}
              onChange={fightingBackCommentChanged}
            />
            {showPublicOfferingDisclaimer && (
              <PublicOfferingDisclaimer
                className="mt-3"
                disabled={status !== 'INITIALIZED'}
                hasConfirmed={hasConfirmedPublicOfferingDisclaimer ?? false}
                onHasConfirmedChanged={hasConfirmedPublicOfferingDisclaimerChanged}
              />
            )}
            {hasValidationErrors && (
              <ValidationErrors tabId={tabId} onlyErrorsDisplayedInSummary errorPrefix="quote.errorMessages." />
            )}
            <WarningCallTrader className="mt-3 mb-3" />
          </div>
          {isNextDocumentEnabled ? (
            <DocumentButton tabId={tabId} className="mx-3 mb-3" workspaceType="dynamic" documentType="TermSheet" />
          ) : (
            <LegacyDocumentButton
              tabId={tabId}
              className="mx-3 mb-3"
              workspaceType="dynamic"
              documentType="TermSheet"
            />
          )}
          <ProductModificationButtons
            canApply={canApply}
            onApply={applyProductModification}
            onReload={reloadProductModification}
            status={status}
            isReady={isReady}
          />
        </div>
      </GenericErrorBoundary>
    </FormOrientationContext.Provider>
  );
};
Component.displayName = 'ProductModificationSidePanel';

export const SidePanel = connectSidePanel(Component);
